import { SENDA } from '@somnhold/content';
import { cellCenter, cellId } from '../grid.ts';
import type { CraneRules, GargRules, GearboxRules, GondolaRules } from '../types.ts';
import type { BoxLayout, GondolaLayout, QuarryLayout } from './cable-layout.ts';

/**
 * The cable's rules from their layouts (docs/sendas/artifice.md, chapter 2): the fight's builder (campaign/sendas.ts)
 * and the renderer's scenes build them the same way. SENDA.garg, crane, gondola and gearbox. The life of what flies and
 * of La Picotera, and Dunna's bolt, in hit points (seconds of D, worked out by the caller).
 */

const garg = (hp: number, crowHp: number, perches: GargRules['perches'], from: { x: number; y: number }): GargRules => {
  const G = SENDA.garg;
  return { hp, crowHp, pace: G.pace, reach: G.reach, reload: G.reload, heroBp: G.heroBp, blows: G.blows, peckR: G.peckR, perches, from: { ...from } };
};

export const craneRules = (L: QuarryLayout, gargHp: number, crowHp: number, dunnaHit: number): { crane: CraneRules; garg: GargRules } => {
  const C = SENDA.crane;
  return {
    crane: {
      winch: { ...L.winch },
      foot: { ...L.foot },
      hook: { ...L.hook },
      heavyX: L.heavyX,
      pieces: C.pieces,
      lower: C.lower,
      tilt: C.tilt,
      fall: C.fall,
      flock: { ...C.flock },
      swing: { ...C.swing, at: { ...L.swingAt } },
      dunna: { every: C.dunna.every, hit: dunnaHit, range: C.dunna.range },
    },
    garg: garg(
      gargHp,
      crowHp,
      L.perches.map((p) => ({ x: p.x, y: p.y, kind: 0, slot: -1 })),
      L.from,
    ),
  };
};

export const gondolaRules = (L: GondolaLayout, gargHp: number, crowHp: number, dunnaHit: number): { gondola: GondolaRules; garg: GargRules } => {
  const G = SENDA.gondola;
  return {
    gondola: {
      holds: L.holds.map((p) => ({ ...p })),
      hold: G.hold,
      fall: G.fall,
      len: G.len,
      stop: G.stop,
      stopFlock: G.stopFlock,
      flock: { ...G.flock },
      tilt: { first: G.tilt.first, every: [G.tilt.every[0], G.tilt.every[1]], warn: G.tilt.warn, dur: G.tilt.dur, slide: G.tilt.slide },
      pylons: [...G.pylons],
      dunnaAt: { ...L.dunnaAt },
      dunna: { every: G.dunna.every, hit: dunnaHit, range: G.dunna.range },
    },
    garg: garg(gargHp, crowHp, [...L.roof.map((p) => ({ x: p.x, y: p.y, kind: 1, slot: -1 })), ...L.cable.map((p) => ({ x: p.x, y: p.y, kind: 2, slot: -1 }))], L.from),
  };
};

/**
 * `radius`: the board's (the layout's + 2: the clearing's outskirts). The cell ids from it, as in the artificer's
 * chapter 1 (docs/RENDIMIENTO.md: with the layout's own they fell off the board).
 */
export const gearboxRules = (L: BoxLayout, radius: number, gargHp: number, crowHp: number, bossHp: number, dunnaHit: number): { gearbox: GearboxRules; garg: GargRules } => {
  const B = SENDA.gearbox;
  const id = (h: { q: number; r: number }): number => cellId(radius, h.q, h.r);
  const slots = L.slots.map(id);
  const lever = id(L.lever);
  // The gargoyles sit on the slots and the lever (a little above each, where they show).
  const perches = [...slots, lever].map((c, i) => {
    const p = cellCenter(radius, c);
    return { x: p.x, y: p.y - 150, kind: 3, slot: i };
  });
  return {
    gearbox: {
      slots,
      lie: L.lie.map(id),
      lever,
      pieces: [...L.pieces],
      input: 60,
      want: L.want,
      way: L.way,
      stand: B.stand,
      pull: B.pull,
      reach: B.reach,
      carryBp: [...B.carryBp],
      flock: { ...B.flock },
      err: { ...B.err },
      haul: B.haul,
      boss: { hp: bossHp, at: { ...L.bossAt }, peck: { ...B.boss.peck }, beat: { ...B.boss.beat }, call: { ...B.boss.call } },
      dunnaAt: { ...L.dunnaAt },
      dunna: { every: B.dunna.every, hit: dunnaHit, range: B.dunna.range },
    },
    garg: garg(gargHp, crowHp, perches, L.from),
  };
};
